import React, { useEffect, useState } from 'react';

import { ist_day } from '../shared/ist';
import FilePanel from './file_panel';
import HolidaysPanel from './holidays_panel';
import ResponsePanel from './response_panel';
import TeacherMonthDialog from './teacher_month_dialog';
import { ClaimsPanel, DeletedPanel, HeldPanel } from './queues';
import { DELETED_VIEW, deleted_section_label } from './deleted_alert';
import './payouts.css';

/*
 * PROD-7828 W4 — the Bank payouts tab (/teacher-payments/bank-payouts). The bank file and the bank response
 * use the header's month; held lines, reports, deleted accounts and holidays span every month. Any
 * teacher name opens that teacher-month's payouts (retry and paid-outside live there).
 * props: pay_month, month_label, now(), on_error(e, fallback) (401 only), on_notify(type, message);
 * D10: view_request {view, rev} opens a section (the page banner), deleted_count labels Deleted accounts, and
 * on_changed() tells the page a drill-down write happened (the banner count re-reads).
 */
const VIEWS = [
  { key: 'file', label: 'Bank file' },
  { key: 'response', label: 'Bank response' },
  { key: 'held', label: 'Held lines' },
  { key: 'claims', label: 'Not received reports' },
  { key: 'deleted', label: 'Deleted accounts' },
  { key: 'holidays', label: 'Bank holidays' }
];
const MONTH_VIEWS = ['file', 'response'];

const BankPayoutsTab = ({
  pay_month,
  month_label,
  now,
  on_error,
  on_notify,
  view_request,
  deleted_count = 0,
  on_changed
}) => {
  const req_view = view_request && view_request.view;
  const req_rev = view_request && view_request.rev;
  const [view, setView] = useState(() =>
    VIEWS.some((v) => v.key === req_view) ? req_view : 'file'
  );
  useEffect(() => {
    if (VIEWS.some((v) => v.key === req_view)) setView(req_view);
  }, [req_view, req_rev]);
  const [open, setOpen] = useState(null); // {teacher_uuid, pay_month}
  // Bumped after a drill-down write so the panel behind it re-reads.
  const [rev, setRev] = useState(0);
  const today = ist_day(now());
  const open_teacher = (teacher_uuid, month) =>
    setOpen({ teacher_uuid, pay_month: month || pay_month });

  return (
    <div className="tp-po-tab">
      <div className="tp-card-hd">
        <span className="tp-card-hd-title" role="heading" aria-level={2}>
          Bank payouts
        </span>
        <span className="tp-card-hd-sub">
          {MONTH_VIEWS.indexOf(view) !== -1 ? month_label : 'All months'}
        </span>
      </div>

      <div className="tp-seg tp-po-seg" role="group" aria-label="Bank payouts section">
        {VIEWS.map((v) => (
          <button
            key={v.key}
            type="button"
            className={view === v.key ? 'on' : ''}
            aria-pressed={view === v.key}
            onClick={() => setView(v.key)}
          >
            {v.key === DELETED_VIEW ? deleted_section_label(v.label, deleted_count) : v.label}
          </button>
        ))}
      </div>

      {view === 'file' ? (
        <FilePanel
          key={`${pay_month}-${rev}`}
          pay_month={pay_month}
          on_open_teacher={open_teacher}
          on_notify={on_notify}
          on_error={on_error}
        />
      ) : null}
      {view === 'response' ? (
        <ResponsePanel
          key={pay_month}
          pay_month={pay_month}
          on_open_teacher={open_teacher}
          on_notify={on_notify}
          on_error={on_error}
        />
      ) : null}
      {view === 'held' ? (
        <HeldPanel
          key={rev}
          today={today}
          on_open_teacher={open_teacher}
          on_notify={on_notify}
          on_error={on_error}
        />
      ) : null}
      {view === 'claims' ? (
        <ClaimsPanel
          key={rev}
          on_open_teacher={open_teacher}
          on_notify={on_notify}
          on_error={on_error}
        />
      ) : null}
      {view === 'deleted' ? (
        <DeletedPanel key={rev} on_open_teacher={open_teacher} on_error={on_error} />
      ) : null}
      {view === 'holidays' ? (
        <HolidaysPanel today={today} on_notify={on_notify} on_error={on_error} />
      ) : null}

      {open ? (
        <TeacherMonthDialog
          pay_month={open.pay_month}
          teacher_uuid={open.teacher_uuid}
          now={now}
          on_close={() => {
            setOpen(null);
          }}
          on_changed={() => {
            setRev((r) => r + 1);
            if (on_changed) on_changed();
          }}
          on_error={on_error}
        />
      ) : null}
    </div>
  );
};

export default BankPayoutsTab;
